import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Server,
  Loader2,
  AlertCircle,
  RefreshCw,
  Wifi,
  WifiOff,
  Trash2,
} from "lucide-react";
import api, { extractError } from "../lib/api";

interface Instance {
  id: string;
  instanceId: string;
  displayName: string | null;
  url: string | null;
  version: string | null;
  status: string;
  lastSeenAt: string | null;
  firstConnectedAt: string;
}

export default function InstancesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery<Instance[]>({
    queryKey: ["instances"],
    queryFn: async () => {
      const res = await api.get("/instances");
      return Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
    },
    refetchInterval: 30000,
  });

  const removeMutation = useMutation({
    mutationFn: async (instanceId: string) => {
      await api.delete(`/instances/${instanceId}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["instances"] }),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Server size={28} className="text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Verbunde Systeme</h1>
            <p className="text-sm text-gray-500">Alle angebundenen LV-Instanzen (Leaf-Systeme)</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          <AlertCircle size={18} className="shrink-0" />
          <span>Systeme konnten nicht geladen werden</span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {isLoading ? (
          <div className="col-span-full flex items-center justify-center py-12">
            <Loader2 size={24} className="animate-spin text-gray-400" />
          </div>
        ) : !data || data.length === 0 ? (
          <div className="col-span-full py-12 text-center">
            <Server size={48} className="mx-auto text-gray-300 mb-3" />
            <p className="text-sm text-gray-500">Keine verbundenen Systeme</p>
            <p className="text-xs text-gray-400 mt-1">Sobald sich ein Leaf-System verbindet, erscheint es hier</p>
          </div>
        ) : (
          data.map((inst) => (
            <div key={inst.id} className="card">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {inst.status === "online" ? (
                      <Wifi size={16} className="text-green-500" />
                    ) : (
                      <WifiOff size={16} className="text-gray-400" />
                    )}
                    <h3 className="font-semibold text-gray-900 truncate">
                      {inst.displayName || inst.instanceId}
                    </h3>
                  </div>
                  <p className="text-xs text-gray-500 mt-1 font-mono">{inst.instanceId}</p>
                </div>
                <button
                  onClick={() => {
                    if (window.confirm(`Instanz "${inst.displayName || inst.instanceId}" wirklich entfernen?`)) {
                      removeMutation.mutate(inst.instanceId);
                    }
                  }}
                  className="btn-ghost p-1.5 text-gray-300 hover:text-red-500 transition-all"
                  title="Entfernen"
                >
                  <Trash2 size={14} />
                </button>
              </div>
              <div className="space-y-1 text-xs text-gray-500">
                {inst.url && (
                  <div className="flex justify-between">
                    <span>URL</span>
                    <span className="text-gray-700 truncate max-w-[200px]">{inst.url}</span>
                  </div>
                )}
                {inst.version && (
                  <div className="flex justify-between">
                    <span>Version</span>
                    <span className="text-gray-700">{inst.version}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span>Status</span>
                  <span className={inst.status === "online" ? "text-green-600 font-medium" : "text-gray-400"}>
                    {inst.status === "online" ? "Online" : "Offline"}
                  </span>
                </div>
                {inst.lastSeenAt && (
                  <div className="flex justify-between">
                    <span>Zuletzt gesehen</span>
                    <span className="text-gray-700">{new Date(inst.lastSeenAt).toLocaleString("de-AT")}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span>Verbunden seit</span>
                  <span className="text-gray-700">{new Date(inst.firstConnectedAt).toLocaleString("de-AT")}</span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
